'use client'

import { Filters } from '@/app/_components/Event/Event/Filters'
import ArrowDown from '@/app/_components/Icon/ArrowDown'
import Timer from '@/app/_components/Icon/Timer'
import I18nTranslator from '@/app/_components/Shared/I18nTranslator/I18nTranslatorClient'
import { useGetEvents } from '@/app/_hooks/useGetEvents'
import { cn } from '@/app/utils'
import { Event, EventStatusEnum, PaginatedEvents } from '@/backend/models/Event'
import clsx from 'clsx'
import dayjs from 'dayjs'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'
import { useMediaQuery } from 'react-responsive'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

const statuses = {
  'in-progress': '진행중',
  upcoming: '모집중',
  ended: '마감',
  all: '',
} as const

export function EventCard({
  event,
  className,
}: {
  event: Event
  className?: string
}) {
  const daysLeft = useMemo(() => {
    if (event.status !== 'in-progress') return 0

    const now = dayjs()
    const date = dayjs.utc(event.endDate).local()

    return date.diff(now, 'day')
  }, [event.endDate, event.status])

  return (
    <Link
      href={`/event/${event.id}`}
      className={cn(
        'relative h-456 w-300 cursor-pointer overflow-hidden rounded-20 shadow-card-mobile transition-transform duration-300 ease-in-out hover:-translate-y-10 hover:shadow-xl xl:shadow-card-web',
        className,
      )}
      aria-hidden="true">
      <div className="flex w-full flex-col overflow-hidden rounded-20 shadow-card-mobile lg:w-300 xl:shadow-card-web">
        <div className="relative aspect-square w-full">
          <Image
            src={event.imageURL}
            fill
            alt={`${event.title} img`}
            className="object-cover object-center"
          />
        </div>
        <div className="flex h-156 flex-col gap-10 p-24">
          <div className="flex h-22 items-center gap-5">
            <span
              className={clsx(
                'flex h-20 items-center justify-center rounded-5 px-10 font-noto-sans-kr text-10 text-white',
                {
                  'bg-french-bleu': event.status !== 'ended',
                  'bg-[#8B8B8B]': event.status === 'ended',
                },
              )}>
              {statuses[event.status]}
            </span>
            {event.status === 'in-progress' && (
              <Timer width={24} height={24} fill="#8B8B8B" />
            )}
            <span className="c-b4">
              {event.status === 'upcoming' && '모집중'}
              {event.status === 'ended' && '결과 발표'}
              {event.status === 'in-progress' && `${daysLeft}일 남음`}
            </span>
          </div>

          <span
            className={twMerge(
              'c-h7',
              event.title.length > 30 ? 'line-clamp-1' : '',
            )}>
            {event.title}
          </span>

          <span className="c-b4 line-clamp-2">{event.eventTarget} 대상</span>
        </div>
      </div>
    </Link>
  )
}

function useLimit() {
  const is2xl = useMediaQuery({
    query: '(min-width: 1440px)',
  })
  const isXl = useMediaQuery({
    query: '(min-width: 1024px)',
  })

  if (typeof window === 'undefined') return 4

  return is2xl ? 8 : isXl ? 6 : 4
}

export default function Events({
  initialData,
  filter,
}: {
  initialData: PaginatedEvents
  filter: z.infer<typeof EventStatusEnum.zEnum>
}) {
  const limit = useLimit()
  const data = initialData.data.slice(0, limit)

  const { items, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useGetEvents({
      initialData: initialData.data,
      limit,
      type: filter,
    })

  return (
    <div className="container">
      <div className="relative flex flex-col gap-20 py-100">
        <div className="flex w-full flex-col justify-between gap-20 lg:flex-row">
          <span className="a-h3 text-french-bleu lg:a-h2">📅 Event</span>

          <Filters />
        </div>

        <div className="flex flex-wrap gap-24 max-lg:justify-center">
          {data.map((e) => (
            <EventCard
              className={'sm:max-[500px]:h-auto sm:max-[500px]:w-full'}
              event={e}
              key={e.id}
            />
          ))}
          {items.map((e) => (
            <EventCard
              className={'sm:max-[500px]:h-auto sm:max-[500px]:w-full'}
              event={e}
              key={e.id}
            />
          ))}
          <div className="flex w-full justify-center">
            {hasNextPage && (
              <button
                type="button"
                className="c-b2 mt-32 flex items-center gap-4"
                disabled={isFetchingNextPage}
                onClick={() => {
                  if (hasNextPage && !isFetchingNextPage) return fetchNextPage()
                }}>
                <div className="text-french-bleu">
                  {<I18nTranslator text="common.viewMoreLabel" />}
                </div>
                <ArrowDown width={24} height={24} fill="#3366B6" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
